// @ts-nocheck
// Shared grade/status helpers for parent-facing progress views (ClearViewPage, ParentAction).
// Colors reference the shared theme tokens in styles/theme.css so status colors stay
// consistent with the rest of the app's brand palette.
import { COLOR_SCORE_PRESETS, colorFromScore } from './grading';
export const UI_COLORS = {
  frame: 'var(--ae-primary)',
  panel: 'var(--ae-border)',
  bg: 'var(--ae-bg)',
  nA: 'var(--ae-status-na)',
  notYet: 'var(--ae-status-not-yet)',
  working: 'var(--ae-status-working)',
  mastered: 'var(--ae-status-mastered)',
  onTrack: 'var(--ae-status-on-track)',
};

export const GRADE_ORDER = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
export const CORE_GRADE_ORDER = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export const SUBJECT_PRIORITY = {
  literacy: 1,
  mathematics: 2,
  math: 2,
  'success skills': 3,
  'social studies': 4,
};

export function normalizedGradeLabel(grade) {
  const g = String(grade || '').toUpperCase();
  if (g === 'PK') return 'Pre-K';
  if (g === 'K') return 'Kindergarten';
  if (!g) return 'N/A';
  const lastGrade = g.split('-').pop();
  return `${g}${lastGrade === '1' ? 'st' : lastGrade === '2' ? 'nd' : lastGrade === '3' ? 'rd' : 'th'} Grade`;
}

export function gradeSort(a, b) {
  const ai = GRADE_ORDER.indexOf(String(a).toUpperCase());
  const bi = GRADE_ORDER.indexOf(String(b).toUpperCase());
  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
}

function normalizeGradeToken(grade) {
  return String(grade || '').trim().toUpperCase().replace(/^(\d+)\s+TO\s+(\d+)$/i, '$1-$2');
}

export function gradeFloor(grade) {
  const token = normalizeGradeToken(grade);
  if (!token) return '';
  if (token.includes('-')) {
    const start = token.split('-')[0]?.trim() || '';
    return CORE_GRADE_ORDER.includes(start) ? start : '';
  }
  return CORE_GRADE_ORDER.includes(token) ? token : '';
}

export function gradeIndex(grade) {
  return CORE_GRADE_ORDER.indexOf(gradeFloor(grade));
}

export function statusFromColor(color) {
  const c = String(color || '').toLowerCase();
  if (c === 'green') return 'mastered';
  if (c === 'lightgreen') return 'on_track';
  if (c === 'yellow') return 'working';
  if (c === 'red') return 'not_yet';
  return 'na';
}

export function statusPriority(status) {
  if (status === 'not_yet') return 4;
  if (status === 'working') return 3;
  if (status === 'on_track') return 2;
  if (status === 'mastered') return 1;
  return 0;
}

// Display status for summary tiles (grade, domain, strand): any active Working standard
// shows the tile as yellow, even when another standard in the group is Not Yet.
export function gradeStatusFromDomains(gradeRow) {
  if (!gradeRow?.domains) return null;
  const domainList = Object.values(gradeRow.domains);
  if (!domainList.length) return null;

  const assessed = domainList
    .map((domain) => summaryStatus(domain))
    .filter((status) => status && status !== 'na');

  if (!assessed.length) return 'na';
  if (assessed.includes('working')) return 'working';
  if (assessed.includes('not_yet')) return 'not_yet';
  if (assessed.includes('on_track')) return 'on_track';
  if (assessed.includes('mastered')) return 'mastered';
  return 'na';
}

export function summaryStatus(row) {
  if (row?.domains && Object.keys(row.domains).length > 0) {
    const domainStatus = gradeStatusFromDomains(row);
    if (domainStatus) return domainStatus;
  }
  if (Object.prototype.hasOwnProperty.call(row || {}, 'aggregateScore')) {
    return statusFromScore(row.aggregateScore);
  }
  const counts = row?.counts || {};
  if (counts.working > 0) return 'working';
  if (counts.not_yet > 0) return 'not_yet';
  if (counts.on_track > 0) return 'on_track';
  if (counts.mastered > 0) return 'mastered';
  return row?.status || 'na';
}

export function statusFromScore(score) {
  if (typeof score !== 'number' || !Number.isFinite(score)) return 'na';
  return statusFromColor(colorFromScore(score));
}

export function averageAssessedScores(scores) {
  const assessed = scores.filter((score) => typeof score === 'number' && Number.isFinite(score));
  return assessed.length ? assessed.reduce((sum, score) => sum + score, 0) / assessed.length : null;
}

export function standardProgressScore(point = {}) {
  if (typeof point.avg_score === 'number' && Number.isFinite(point.avg_score)) return point.avg_score;
  if (typeof point.score === 'number' && Number.isFinite(point.score)) return point.score;
  // Legacy color-only entries use the same score presets as the entry form.
  return COLOR_SCORE_PRESETS[String(point.color || '').toLowerCase()] ?? null;
}

export function aggregateFamilyGrade(gradeRow) {
  Object.values(gradeRow.domains).forEach((domain) => {
    Object.values(domain.strands).forEach((strand) => {
      const standards = [...new Map(Object.values(strand.clusters)
        .flatMap((cluster) => cluster.standards)
        .map((standard) => [standard.standardCode, standard])).values()];
      strand.aggregateScore = averageAssessedScores(standards.map((standard) => standard.aggregateScore));
      strand.status = statusFromScore(strand.aggregateScore);
    });
    domain.aggregateScore = averageAssessedScores(Object.values(domain.strands).map((strand) => strand.aggregateScore));
    domain.status = statusFromScore(domain.aggregateScore);
  });
  gradeRow.aggregateScore = averageAssessedScores(Object.values(gradeRow.domains).map((domain) => domain.aggregateScore));
  gradeRow.status = gradeStatusFromDomains(gradeRow) || statusFromScore(gradeRow.aggregateScore);
  gradeRow.strands.forEach((strand) => {
    strand.aggregateScore = averageAssessedScores(Object.values(gradeRow.domains)
      .map((domain) => domain.strands[strand.label]?.aggregateScore));
    strand.status = statusFromScore(strand.aggregateScore);
  });
  return gradeRow;
}

export function statusColor(status) {
  if (status === 'not_yet') return UI_COLORS.notYet;
  if (status === 'working') return UI_COLORS.working;
  if (status === 'mastered') return UI_COLORS.mastered;
  if (status === 'on_track') return UI_COLORS.onTrack;
  return UI_COLORS.nA;
}

export function canonicalSubject(name) {
  const lower = String(name || '').toLowerCase();
  if (lower.includes('literacy') || lower.includes('reading')) return 'Literacy';
  if (lower.includes('math')) return 'Math';
  if (lower.includes('success')) return 'Success Skills';
  return name || 'Subject';
}

const STRAND_CODE_LABELS = {
  RF: 'Reading Foundations',
  RL: 'Reading Literary Text',
  RI: 'Reading Informational Text',
  W: 'Writing',
  SL: 'Speaking and Listening',
  L: 'Language',
  NO: 'Number Sense and Operations',
  OA: 'Operations and Algebraic Thinking',
  MD: 'Measurement and Data',
  G: 'Geometry',
  NS: 'Number Sense',
  NBT: 'Number Sense and Operations in Base Ten',
  NF: 'Number Sense and Operations in Fractions',
  RA: 'Relationships and Algebraic Thinking',
  EEI: 'Expressions, Equations and Inequalities',
  RP: 'Ratios and Proportional Relationships',
  F: 'Functions',
  GM: 'Geometry and Measurement',
  DS: 'Data and Statistics',
  DSP: 'Data Analysis, Statistics and Probability',
  ESS: 'Earth and Space Science',
  LS: 'Life Science',
  PS: 'Physical Science',
  ETS: 'Engineering, Technology, and Applications of Science',
  SED: 'Social and Emotional Development',
  AD: 'Academic Development',
  CD: 'Career Development',
};

// Progression rows can ship without strand_description, or with a bare code such as
// "SED" or a cluster number, none of which are readable strand names on their own.
export function resolveStrandLabel(standard = {}, fallback = 'General') {
  const description = String(standard.strand_description || '').trim();
  if (description) return description;

  const code = String(standard.strand || '').trim();
  if (code) {
    const mapped = STRAND_CODE_LABELS[code.toUpperCase()];
    if (mapped) return mapped;
    if (!/^\d+$/.test(code)) return code;
  }

  const cluster = String(standard.cluster_expectation_description || '').trim();
  if (cluster) return cluster;

  const domain = String(standard.domain_description || standard.domain || '').trim();
  if (domain) return domain;

  return fallback;
}

export function resolveClusterLabel(standard = {}) {
  const description = String(standard.cluster_expectation_description || '').trim();
  const clusterCode = String(standard.cluster_and_expectation_number || '').trim();
  if (String(standard.subject || '').trim().toLowerCase() === 'science') {
    const standardCode = String(standard.mls_code || '').trim();
    const isCode = !description
      || description === clusterCode
      || description === standardCode
      || /^(?:[A-Z]{1,4}\d*|\d+|\d+(?:-\d+)?[.-][A-Z]+\d*(?:[.-][A-Z0-9]+)*)$/.test(description);
    if (isCode) {
      return String(standard.strand_description || standard.domain_description || 'Science').trim();
    }
  }
  return description || clusterCode || 'Cluster';
}

export function normalizeLiteracyWritingStrand(subject, grade, standardCode, sourceLabel = '') {
  if (canonicalSubject(subject) !== 'Literacy') return null;

  const code = String(standardCode || '').trim().toUpperCase();
  if (!/\.W\./.test(code)) return null;

  const label = String(sourceLabel || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (label.includes('research')) return 'Research';
  if (label.includes('revise') || label.includes('edit')) return 'Revise and Edit';

  const gradeLevel = gradeIndex(grade);
  const writingGroup = code.match(/\.W\.(1|2|3)\./)?.[1];

  if (gradeLevel >= gradeIndex('6')) {
    return {
      1: 'Research',
      2: 'Development',
      3: 'Revise and Edit',
    }[writingGroup] || null;
  }

  if (/\.W\.3\./.test(code)) return 'Research';
  if (/\.W\.1\.C\./.test(code)) return 'Revise and Edit';
  return 'Development';
}
